/**
 * BUSCA, ORDENAÇÃO, RECORTE e RESUMO da lista de clientes.
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`: são regras que
 * decidem QUAIS pessoas o prestador vê e em que ordem, e todas erram em
 * silêncio — uma lista recortada errado não dá erro, só esconde cliente.
 *
 * ─── O QUE QUEBRA SE FOR ESCRITO NA PÁGINA ──────────────────────────────────
 *
 * · **Acento** (#190d). A busca era `name.toLowerCase().includes(...)` cru, e o
 *   app é pt-BR com nome digitado à mão: quem procura "conceicao" no teclado do
 *   celular recebia ZERO por "Conceição" e a tela parecia dizer que o cliente
 *   não existe. `normalizeSearchText` dobra caixa E acento.
 * · **Telefone e documento são DÍGITOS, não texto.** O cadastro guarda
 *   "(11) 99988-7777" e a pessoa digita "99988" — comparar como texto não casa
 *   por causa da máscara. Por isso, quando o termo tem dígito, ele também é
 *   comparado contra os dígitos de telefone e documento.
 * · **Ordem instável** faz linhas trocarem de lugar sozinhas entre renders
 *   quando há empate (dois "Silva", duas datas iguais). Todo comparador
 *   desempata por `id` (#93).
 * · **`localeCompare` sobre o texto CRU** ordena "Ângela" depois de "Zuleica"
 *   quando o `localeCompare` cai na tabela ASCII. A ordenação passa pelo mesmo
 *   normalizador da busca.
 */
import type { Client } from '@/types/database';
import { normalizeSearchText } from '@/lib/quoteSearch';
import type { ReportDateRange, ReportPeriodPreset } from '@/lib/reportPeriod';

/** O que a lista realmente lê de um cliente (o cache devolve linhas parciais). */
export type ClientRow = Partial<Client> & { id: string; name: string };

const onlyDigits = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number' ? String(value).replace(/\D/g, '') : '';

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo (é o estado sem filtro).
 *
 * Campos varridos: nome, e-mail, endereço em linha única, município — por
 * TEXTO; telefone e documento — por DÍGITO. Buscar por cidade importa para
 * quem atende bairro/região e é o jeito natural de perguntar "quem eu tenho no
 * Centro?".
 */
export function clientMatchesSearch(client: ClientRow, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  if (normalizeSearchText(client.name).includes(needle)) return true;
  if (normalizeSearchText(client.email).includes(needle)) return true;
  if (normalizeSearchText(client.address).includes(needle)) return true;
  if (normalizeSearchText(client.municipio_nome).includes(needle)) return true;

  const digits = onlyDigits(term);
  if (digits) {
    if (onlyDigits(client.phone).includes(digits)) return true;
    if (onlyDigits(client.document).includes(digits)) return true;
  }
  return false;
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type ClientSortOption = 'name' | 'nameDesc' | 'recent' | 'oldest';

export const CLIENT_SORT_LABELS: Record<ClientSortOption, string> = {
  name: 'Nome (A–Z)',
  nameDesc: 'Nome (Z–A)',
  recent: 'Cadastro mais recente',
  oldest: 'Cadastro mais antigo',
};

export const CLIENT_SORT_ORDER: readonly ClientSortOption[] = [
  'name',
  'nameDesc',
  'recent',
  'oldest',
];

/**
 * A–Z é o PADRÃO porque é como a tela sempre se comportou (a consulta era
 * `.order('name')`). Trocar o padrão junto com o redesenho mudaria a lista de
 * todo mundo sem ninguém ter pedido.
 */
export const DEFAULT_CLIENT_SORT: ClientSortOption = 'name';

function timeOf(client: ClientRow): number {
  const parsed = new Date(client.created_at ?? '').getTime();
  // Data ilegível vai para o FIM em "mais recentes" — exceção não ocupa a
  // primeira linha da lista.
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Vazio vai para o FIM (`￿`): um punhado de "sem nome" no topo esconde o resto. */
function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

/** Não muta a entrada: a lista da tela é derivada. */
export function sortClientsBy<T extends ClientRow>(
  clients: readonly T[],
  option: ClientSortOption,
): T[] {
  const rows = [...clients];
  const byId = (a: T, b: T) => String(a.id).localeCompare(String(b.id));

  switch (option) {
    case 'nameDesc':
      return rows.sort((a, b) => textOf(b.name).localeCompare(textOf(a.name)) || byId(a, b));
    case 'recent':
      return rows.sort((a, b) => timeOf(b) - timeOf(a) || byId(a, b));
    case 'oldest':
      return rows.sort((a, b) => timeOf(a) - timeOf(b) || byId(a, b));
    case 'name':
    default:
      return rows.sort((a, b) => textOf(a.name).localeCompare(textOf(b.name)) || byId(a, b));
  }
}

// ─── Recorte por CONTATO (os botões da faixa) ───────────────────────────────

/**
 * O análogo do STATUS de `/quotes`: o recorte intrínseco de uma lista de
 * pessoas não é um ciclo de vida, é **conseguir falar com elas**. "Sem contato"
 * é o balde acionável — são os cadastros que não servem para nada até alguém
 * completar o telefone.
 */
export type ClientContactFilter = 'all' | 'phone' | 'address' | 'noContact';

export const CLIENT_CONTACT_FILTERS: readonly { value: ClientContactFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'phone', label: 'Com telefone' },
  { value: 'address', label: 'Com endereço' },
  { value: 'noContact', label: 'Sem contato' },
];

const hasPhone = (client: ClientRow): boolean => onlyDigits(client.phone).length > 0;
const hasAddress = (client: ClientRow): boolean =>
  typeof client.address === 'string' && client.address.trim().length > 0;
const hasEmail = (client: ClientRow): boolean =>
  typeof client.email === 'string' && client.email.trim().length > 0;

export function matchesContactFilter(client: ClientRow, filter: ClientContactFilter): boolean {
  switch (filter) {
    case 'phone':
      return hasPhone(client);
    case 'address':
      return hasAddress(client);
    // "Sem contato" é sem telefone E sem e-mail: quem tem só e-mail ainda é
    // alcançável, e listá-lo aqui mandaria o usuário completar um cadastro que
    // já serve.
    case 'noContact':
      return !hasPhone(client) && !hasEmail(client);
    case 'all':
    default:
      return true;
  }
}

/**
 * Quantos cada botão traria — contados sobre o recorte JÁ filtrado por busca e
 * período, mas ANTES do contato. É a única definição que não mente: contar
 * sobre a lista inteira faria o botão prometer 12 com uma busca ativa que
 * devolve 3 (#193).
 */
export function countClientsByContact(
  clients: readonly ClientRow[],
): Record<ClientContactFilter, number> {
  const counts: Record<ClientContactFilter, number> = {
    all: clients.length,
    phone: 0,
    address: 0,
    noContact: 0,
  };
  for (const client of clients) {
    if (hasPhone(client)) counts.phone += 1;
    if (hasAddress(client)) counts.address += 1;
    if (!hasPhone(client) && !hasEmail(client)) counts.noContact += 1;
  }
  return counts;
}

// ─── Período de CADASTRO ────────────────────────────────────────────────────

/** `range` nulo = sem filtro. Data ilegível NÃO é recortada (ver #193-d). */
export function matchesClientPeriod(client: ClientRow, range: ReportDateRange | null): boolean {
  if (!range) return true;
  const time = new Date(client.created_at ?? '').getTime();
  if (!Number.isFinite(time)) return true;
  return time >= range.from.getTime() && time <= range.to.getTime();
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * O contato fica de FORA porque ele tem os botões visíveis logo acima: contar
 * um filtro que a tela já mostra faria o selo dizer "1" com o modal em branco.
 */
export function countActiveClientFilters(period: ReportPeriodPreset | null): number {
  return period ? 1 : 0;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

export interface ClientListSummary {
  /** Pessoas no recorte visível — tem que bater com a lista logo abaixo. */
  count: number;
  /** Quantas dão para chamar (telefone cadastrado). */
  withPhone: number;
  /** Quantas não dão para chamar por meio nenhum. */
  withoutContact: number;
}

export function summarizeClientList(clients: readonly ClientRow[]): ClientListSummary {
  let withPhone = 0;
  let withoutContact = 0;
  for (const client of clients) {
    if (hasPhone(client)) withPhone += 1;
    else if (!hasEmail(client)) withoutContact += 1;
  }
  return { count: clients.length, withPhone, withoutContact };
}

/**
 * O RÓTULO do número do hero — é ele que torna o número honesto.
 *
 * O valor ao vivo desta tela é a CONTAGEM do recorte, e ela muda enquanto o
 * usuário digita. Sem dizer de qual recorte ele saiu, "3" sob o título
 * "Clientes" lê como "tenho 3 clientes" quando pode ser o resultado de uma
 * busca por "Silva" (o corolário do #182, aplicado a um número que não é
 * dinheiro).
 */
export function clientListTotalLabel(filtered: boolean): string {
  return filtered ? 'Clientes no filtro' : 'Clientes cadastrados';
}
